import { useSearchParams } from 'react-router-dom';
import { Arrival as Line } from '@beyond-js/ui';
import { Arrival } from '@beyond-js/ui/react';
import { config } from '@snapshots/config';
import { usePresence } from '@snapshots/session';
import { useFrame } from './frame';
import { Way } from './way';

/**
 * The arrival line of a page opened from another product (`?from=<product>`, decision D54): "Opened
 * from {product} · Back to {product}", the first line of the page, leading to the exact place the
 * person came from as `Way` finds it (Packages at the capture's organization and project, PX-09). Dismissing
 * it takes `from` out of the address. A product this application cannot place draws nothing. Until the page
 * knows its place (`waiting`) and the bar has read its descriptor, the line holds its place unseen.
 */
export function Origin({ project, organization, waiting = false }: { project?: string; organization?: string; waiting?: boolean }) {
	const { language } = usePresence();
	const { descriptor } = useFrame();
	const [parameters, setParameters] = useSearchParams();
	const from = parameters.get(Way.PARAMETER);
	const way = Way.find(from, { descriptor, project, organization, projects: config.projects || undefined, packages: config.packages || undefined });
	const labels = language === 'es' ? Line.labels.es : Line.labels.en;
	// While the page or the bar still reads what the way back is built from, the line holds its place, unseen and
	// leading nowhere, so it never arrives above what is drawn (D185); a product it cannot place holds nothing
	const named = Way.named(from);
	if (!way && named && (waiting || descriptor === null)) {
		return (
			<div data-arrival="holding" aria-hidden="true" className="invisible" {...{ inert: "" }}>
				{/* With its Dismiss, as the line it stands for, so the two are one height */}
				<Arrival product={named} href="#" labels={labels} onDismiss={() => undefined} />
			</div>
		);
	}
	if (!way) return null;
	const dismiss = () => {
		const rest = new URLSearchParams(parameters);
		rest.delete(Way.PARAMETER);
		setParameters(rest, { replace: true });
	};
	return (
		<div data-arrival={way.product}>
			<Arrival product={way.name} href={way.href} labels={labels} onDismiss={dismiss} />
		</div>
	);
}
